<template>
	<v-menu v-model="menu" :close-on-content-click="false" location="bottom end" origin="top end" offset="4" max-width="310">
		<template #activator="{ props }">
			<v-text-field
				v-bind="props"
				class="planning-input-dense"
				density="compact"
				append-inner-icon="mdi-menu-down"
				variant="outlined"
				:placeholder="summary ? '' : $t(label)"
				readonly
				hide-details
			>
				<template #prepend-inner>
					<span v-if="summary" class="mr-2">
						{{ $t('core_chart_violations') }}
					</span>

					<v-chip v-if="summary" class="indicator-chip" variant="flat">
						{{ summary }}
					</v-chip>
				</template>
			</v-text-field>
		</template>

		<v-card>
			<v-list>
				<div v-for="opt in options" :key="opt.value" class="mb-3">
					<v-radio :model-value="localValue.status === opt.value" :label="$t(opt.label)" color="primary-green" @click="selectStatus(opt.value)" />

					<div v-if="localValue.status === opt.value && opt.category?.length" class="ml-8 mt-2">
						<div v-if="showSelectAll(opt)" class="d-flex align-center justify-space-between mb-2">
							<v-checkbox
								:model-value="isAllSelected(opt)"
								@update:model-value="toggleSelectAll(opt, $event)"
								:label="$t('action_select_all')"
								density="compact"
								hide-details
								center-affix
								color="primary-green"
								:indeterminate="isGroupIndeterminate(opt)"
								indeterminate-icon="mdi-close-box"
								class="checkbox-gap"
							/>

							<span class="text-caption text-medium-emphasis">
								{{ localValue.selected.length }}
								of
								{{ opt.category.length }}
							</span>
						</div>

						<v-text-field
							v-if="showSelectAll(opt)"
							v-model="search"
							density="compact"
							variant="outlined"
							append-inner-icon="mdi-magnify"
							placeholder="Search"
							hide-details
							class="mb-2"
						/>

						<div v-for="(children, type) in getGroupedChildren(opt)" :key="type" class="mb-3">
							<div v-if="type !== OTHER" class="text-subtitle-2 font-light text-grey-500 mb-2">
								{{ type.charAt(0).toUpperCase() + type.slice(1) }}
							</div>

							<v-checkbox
								v-for="child in children"
								:key="child.value"
								v-model="localValue.selected"
								:label="$t(child.label)"
								:value="child.value"
								density="compact"
								hide-details
								color="primary-green"
							/>
						</div>
					</div>
				</div>
			</v-list>

			<v-divider class="my-2" />

			<v-card-actions class="d-flex justify-end px-4 pb-2">
				<v-btn variant="text" size="small" @click="reset">
					{{ $t('adapter_manage_reset') }}
				</v-btn>

				<v-btn color="primary-green" size="small" @click="apply">
					{{ $t('action_apply') }}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { OTHER } from '../../constants';
import { FilterOption, StatusFilterValue } from '@/rennovation/planning-view/types/components/types';

const MAX_VISIBLE_CHILDREN = 3;

const props = defineProps<{
	label?: string;
	options: FilterOption[];
	modelValue: StatusFilterValue;
}>();

const emit = defineEmits<{
	'update:modelValue': [value: StatusFilterValue];
	apply: [value: StatusFilterValue];
}>();

const { t } = useI18n();

const menu = ref(false);
const search = ref('');

const cloneValue = (val: StatusFilterValue): StatusFilterValue => ({
	status: val?.status ?? null,
	selected: val?.selected ? [...val.selected] : [],
});

const localValue = ref<StatusFilterValue>(cloneValue(props.modelValue));

// Sync state if parent props change externally
watch(
	() => props.modelValue,
	(val) => {
		localValue.value = cloneValue(val);
	},
	{ deep: true }
);

// Revert local changes when menu closes without clicking apply
watch(menu, (isOpen) => {
	if (!isOpen) {
		localValue.value = cloneValue(props.modelValue);
		search.value = '';
	}
});

// Summary chip reads strictly from COMMITTED props.modelValue
const summary = computed(() => {
	const count = props.modelValue?.selected?.length || 0;
	return count ? count : '';
});

const selectStatus = (status: StatusFilterValue['status']) => {
	if (localValue.value.status === status) {
		return;
	}

	localValue.value.status = status;

	const selectedOption = props.options.find((opt) => opt.value === status);
	if (selectedOption?.category?.length) {
		localValue.value.selected = selectedOption.category.map((child) => child.value);
	} else {
		localValue.value.selected = [];
	}

	search.value = '';
};

const getFilteredChildren = (option: FilterOption): FilterOption[] => {
	if (!option.category) {
		return [];
	}

	if (!search.value) {
		return option.category;
	}

	return option.category.filter((child) => child.label.toLowerCase().includes(search.value.toLowerCase()));
};

const getGroupedChildren = (option: FilterOption) => {
	return getFilteredChildren(option).reduce<Record<string, FilterOption[]>>((groups, child) => {
		const type = child.type || OTHER;

		if (!groups[type]) {
			groups[type] = [];
		}

		groups[type].push(child);

		return groups;
	}, {});
};

const showSelectAll = (option: FilterOption) => {
	return (option.category?.length ?? 0) > MAX_VISIBLE_CHILDREN;
};

const isAllSelected = (option: FilterOption) => {
	if (!option.category?.length) {
		return false;
	}

	return option.category.every((child) => localValue.value.selected.includes(child.value));
};

const toggleSelectAll = (option: FilterOption, checked: boolean) => {
	if (!option.category) {
		return;
	}

	localValue.value.selected = checked ? option.category.map((child) => child.value) : [];
};

const isGroupIndeterminate = (option: FilterOption) => {
	if (!option.category?.length) {
		return false;
	}

	const count = option.category.filter((child) => localValue.value.selected.includes(child.value)).length;

	return count > 0 && count < option.category.length;
};

const reset = () => {
	localValue.value = {
		status: null,
		selected: [],
	};

	search.value = '';
	apply();
};

const apply = () => {
	const payload: StatusFilterValue = cloneValue(localValue.value);

	emit('update:modelValue', payload);
	emit('apply', payload);

	menu.value = false;
};
</script>

<style scoped>
.planning-input-dense :deep(.v-field__input) {
	min-height: 32px;
	font-size: 0.75rem;
}

.v-list {
	border-radius: 8px;
	min-height: 100% !important;
	height: 100%;
	padding-top: 12px;
	padding-bottom: 8px;
	padding-left: 12px;
	padding-right: 4px;
}

.text-caption {
	font-size: 0.7rem;
}

input[type='checkbox' i] {
	accent-color: #0f766e !important;
}

:deep(.v-overlay__content) {
	max-width: calc(100vw - 32px);
}

.v-card {
	width: 310px;
	max-width: calc(100vw - 32px);
}

.v-checkbox {
	font-size: 14px !important;
}

:deep(.v-label) {
	margin-left: 12px !important;
}

:deep(.v-selection-control__input) {
	color: #10565c !important;
	opacity: 1 !important;
}

:deep(.v-selection-control__input > .v-icon) {
	opacity: 1 !important;
}

.indicator-chip {
	width: 25px;
	height: 25px;
	background-color: #10565c;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
}

.option-children {
	padding-left: 20px;
	border-left: 1px solid rgb(var(--v-theme-grey-200));
	margin-left: 16px;
	margin-top: 4px;
}

.option-header {
	margin-bottom: 8px;
}

.checkbox-gap {
	margin-left: -8px;
}

:deep(.v-radio) {
	margin-bottom: 4px;
}

:deep(.v-radio .v-selection-control) {
	min-height: 32px;
}

:deep(.v-checkbox .v-selection-control) {
	min-height: 28px;
}
</style>
